import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Play, Eye, MousePointerClick, TrendingUp, Mail, Lightbulb, ArrowRight } from "lucide-react";
import { toast } from "sonner";

/**
 * HomilyDigestPanel — surfaces the weekly open + click rate on the
 * Homily Prep digest so staff know whether pastors are actually reading it.
 *
 * Data comes from GET /admin/homily-digest/analytics?weeks=8. Rendered on
 * the Emails page beside the other automation panels.
 *
 * Design choices:
 * - 4-metric header (sent, unique opens, unique clicks, open-rate %) —
 *   these are the four numbers the outreach lead pulls into her weekly
 *   report card.
 * - Sparkline-style week list underneath — plum bars for open rate, gold
 *   inline count for clicks. No d3, no chart lib — inline bars in Tailwind.
 * - Per-language breakdown row so we know if the ES translation is
 *   pulling its weight versus the EN mainline.
 * - "Top clicked bills" list on the right — the actionable signal
 *   ("HB26-1087 clicked 4 times → keep leaning in on that framing").
 */
export default function HomilyDigestPanel() {
  const [data, setData] = useState(null);
  const [retargeting, setRetargeting] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [analytics, retg] = await Promise.all([
        api.get("/admin/homily-digest/analytics", { params: { weeks: 8 } }),
        api.get("/admin/homily-digest/retargeting", { params: { weeks: 4 } }),
      ]);
      setData(analytics.data);
      setRetargeting(retg.data);
    } catch (err) {
      if (err?.response?.status === 403) return;
      toast.error("Homily digest analytics unavailable");
    }
  };
  useEffect(() => { load(); }, []);

  const sendNow = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/homily-digest/send-now");
      toast.success(`Sent to ${r.data.homily_digest} of ${r.data.recipients} priests.`);
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Send failed";
      toast.error(msg);
    } finally { setBusy(false); }
  };

  if (!data) return null;
  const { weeks = [], totals = {}, top_bills = [] } = data;
  const maxRate = Math.max(1, ...weeks.map(w => w.open_rate));

  return (
    <div className="bg-white border border-ccc-rule p-6 mb-6" data-testid="homily-digest-panel">
      <div className="flex items-end justify-between mb-5">
        <div>
          <div className="eyebrow mb-1">Homily Prep Digest</div>
          <h2 className="font-serif text-2xl text-ccc-plum">Are pastors reading?</h2>
          <div className="text-ccc-muted mt-1" style={{ fontSize: "13px" }}>
            Friday 7 AM Denver · Opted-in priests · Curated CST per bill
          </div>
        </div>
        <button
          onClick={sendNow}
          disabled={busy}
          data-testid="run-homily-digest"
          className="uppercase tracking-[0.08em] px-3 py-1.5 border border-ccc-rule hover:border-ccc-plum inline-flex items-center gap-1 disabled:opacity-40"
          style={{ fontSize: "13px" }}
        >
          <Play size={12}/> {busy ? "Sending…" : "Run Now"}
        </button>
      </div>

      {/* Four-metric header — the numbers the outreach lead reads first. */}
      <div className="grid grid-cols-4 gap-3 mb-6">
        <Metric icon={<Mail size={14}/>} label="Sent (window)" value={totals.sent} testId="stat-hd-sent"/>
        <Metric icon={<Eye size={14}/>} label="Unique opens" value={totals.opens_unique} testId="stat-hd-opens"/>
        <Metric icon={<MousePointerClick size={14}/>} label="Unique clicks" value={totals.clicks_unique} testId="stat-hd-clicks"/>
        <Metric icon={<TrendingUp size={14}/>} label="Open rate" value={`${totals.open_rate}%`} testId="stat-hd-open-rate"/>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2">
          <div className="eyebrow mb-2" style={{ fontSize: "12px" }}>Weekly open rate — last 8 weeks</div>
          {weeks.length === 0 ? (
            <div className="text-ccc-muted py-6" style={{ fontSize: "14px" }}>
              No sends yet. Opt priests in from their contact page, then hit <em>Run Now</em>.
            </div>
          ) : (
            <ul className="space-y-2" data-testid="hd-week-list">
              {weeks.map(w => (
                <li key={w.week} className="grid grid-cols-[70px_1fr_auto] gap-3 items-center" data-testid={`hd-week-${w.week}`}>
                  <div className="font-mono text-ccc-muted" style={{ fontSize: "13px" }}>{w.week}</div>
                  {/* Inline bar — plum-fill = open rate %, gold-tick = clicks/opens engagement */}
                  <div className="h-6 bg-ccc-paper border border-ccc-rule relative overflow-hidden">
                    <div
                      className="h-full bg-ccc-plum"
                      style={{ width: `${Math.min(100, (w.open_rate / maxRate) * 100)}%` }}
                    />
                    {w.clicks_per_open > 0 && (
                      <div
                        className="absolute top-0 h-full w-[3px] bg-ccc-gold"
                        style={{ left: `${Math.min(100, (w.clicks_per_open / 100) * (w.open_rate / maxRate) * 100)}%` }}
                        title={`${w.clicks_per_open}% of openers clicked`}
                      />
                    )}
                  </div>
                  <div className="text-right" style={{ fontSize: "14px" }}>
                    <span className="font-semibold text-ccc-plum">{w.open_rate}%</span>
                    <span className="text-ccc-muted mx-1">·</span>
                    <span className="text-ccc-muted">{w.sent} sent</span>
                    {(w.by_lang?.es?.sent || 0) > 0 && (
                      <span className="text-ccc-muted ml-2" style={{ fontSize: "12px" }}>
                        ES {w.by_lang.es.sent}
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <div className="eyebrow mb-2" style={{ fontSize: "12px" }}>Top clicked bills</div>
          {top_bills.length === 0 ? (
            <div className="text-ccc-muted italic" style={{ fontSize: "13px" }}>
              No clicks tracked yet.
            </div>
          ) : (
            <ul className="space-y-1" data-testid="hd-top-bills">
              {top_bills.slice(0, 6).map((b, i) => {
                // Show the bill path only — hostnames add noise for a
                // Colorado staffer who reads this list at a glance.
                let label = b.url;
                try { label = new URL(b.url).pathname; } catch {}
                return (
                  <li key={i} className="flex items-center justify-between gap-2 border-b border-ccc-rule pb-1">
                    <a href={b.url} target="_blank" rel="noopener noreferrer"
                       className="text-ccc-plum hover:text-ccc-plum-deep truncate"
                       style={{ fontSize: "13px" }}>
                      {label}
                    </a>
                    <span className="font-semibold text-ccc-plum" style={{ fontSize: "13px" }}>
                      {b.clicks}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      <div className="mt-5 pt-4 border-t border-ccc-rule text-ccc-muted italic" style={{ fontSize: "12px" }}>
        Opens counted once per (send, week); a pastor forwarding the digest to a curate
        counts once per unique inbox. Click rate is unique senders who clicked at least one bill.
      </div>

      {/* ─────────── Retargeting suggestions (iter42) ─────────────────
          Bills the pastor cohort has clicked >=2 times BUT that don't
          currently carry an Active Action Alert. Curators still decide —
          this is a hint, not automation. */}
      {retargeting && retargeting.suggestions && retargeting.suggestions.length > 0 && (
        <div className="mt-6 pt-5 border-t border-ccc-rule" data-testid="hd-retargeting">
          <div className="flex items-start gap-2 mb-3">
            <Lightbulb size={14} className="text-ccc-gold-dark mt-1"/>
            <div>
              <div className="eyebrow" style={{ fontSize: "12px" }}>Suggestions for next Action Alert cycle</div>
              <div className="text-ccc-body" style={{ fontSize: "13px" }}>
                Bills pastors have been clicking in the last {retargeting.weeks_covered.length} weeks that don't currently carry an alert.
              </div>
            </div>
          </div>
          <ul className="space-y-2" data-testid="hd-retargeting-list">
            {retargeting.suggestions.slice(0, 5).map((s) => (
              <li key={s.bill_id}
                  className="flex items-center justify-between gap-3 border border-ccc-rule bg-ccc-paper p-3"
                  data-testid={`hd-retg-${s.bill_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-ccc-muted" style={{ fontSize: "12px" }}>{s.number} · CCC {s.position || "Watch"}</div>
                  <div className="font-serif text-ccc-plum truncate" style={{ fontSize: "16px" }}>{s.title}</div>
                </div>
                <div className="shrink-0 flex items-center gap-3">
                  <div className="text-ccc-plum font-semibold" style={{ fontSize: "13px" }}>
                    {s.unique_pastors_clicked} <span className="text-ccc-muted font-normal">pastors</span>
                  </div>
                  <a
                    href={`/admin/campaigns?new_alert_bill_id=${s.bill_id}`}
                    data-testid={`hd-retg-new-${s.bill_id}`}
                    className="inline-flex items-center gap-1 uppercase tracking-[0.08em] text-ccc-plum hover:text-ccc-plum-deep font-semibold"
                    style={{ fontSize: "12px" }}
                  >
                    Draft alert <ArrowRight size={12}/>
                  </a>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Metric({ icon, label, value, testId }) {
  return (
    <div className="border border-ccc-rule p-3 bg-ccc-paper" data-testid={testId}>
      <div className="flex items-center gap-1 text-ccc-muted uppercase tracking-[0.14em] font-semibold" style={{ fontSize: "11px" }}>
        {icon} {label}
      </div>
      <div className="font-serif text-3xl text-ccc-plum mt-1">{value}</div>
    </div>
  );
}
